<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import SimpleBanner from '@/components/overview/SimpleBanner.vue';
import Button from 'primevue/button';

const props = defineProps({
    banners: {
        type: Array,
        required: true
    },
    autoPlayInterval: {
        type: Number,
        default: 5000 // 5 seconds
    }
});

const emit = defineEmits(['banner-click', 'banner-dismiss']);

const currentIndex = ref(0);
const autoPlayTimer = ref(null);
const isPaused = ref(false);

// Filter out dismissed banners
const visibleBanners = ref([...props.banners]);

const currentBanner = computed(() => {
    if (visibleBanners.value.length === 0) return null;
    if (currentIndex.value >= visibleBanners.value.length) {
        currentIndex.value = 0;
    }
    return visibleBanners.value[currentIndex.value];
});

const hasBanners = computed(() => visibleBanners.value.length > 0);
const hasMultipleBanners = computed(() => visibleBanners.value.length > 1);

function next() {
    if (!hasMultipleBanners.value) return;
    currentIndex.value = (currentIndex.value + 1) % visibleBanners.value.length;
    resetAutoPlay();
}

function prev() {
    if (!hasMultipleBanners.value) return;
    currentIndex.value = currentIndex.value === 0
        ? visibleBanners.value.length - 1
        : currentIndex.value - 1;
    resetAutoPlay();
}

function goToSlide(index) {
    currentIndex.value = index;
    resetAutoPlay();
}

function startAutoPlay() {
    if (!hasMultipleBanners.value) return;

    stopAutoPlay();
    autoPlayTimer.value = setInterval(() => {
        if (!isPaused.value) {
            next();
        }
    }, props.autoPlayInterval);
}

function stopAutoPlay() {
    if (autoPlayTimer.value) {
        clearInterval(autoPlayTimer.value);
        autoPlayTimer.value = null;
    }
}

function resetAutoPlay() {
    if (isPaused.value) return;
    stopAutoPlay();
    startAutoPlay();
}

function pause() {
    isPaused.value = true;
    stopAutoPlay();
}

function resume() {
    isPaused.value = false;
    startAutoPlay();
}

function handleBannerClick(banner) {
    emit('banner-click', banner);
}

function handleBannerDismiss(banner) {
    emit('banner-dismiss', banner);

    // Remove the dismissed banner from visible banners
    const index = visibleBanners.value.findIndex(b => b.id === banner.id);
    if (index > -1) {
        visibleBanners.value.splice(index, 1);

        // Adjust current index if needed
        if (currentIndex.value >= visibleBanners.value.length) {
            currentIndex.value = Math.max(0, visibleBanners.value.length - 1);
        }

        // Restart auto-play if we still have banners
        if (visibleBanners.value.length > 1) {
            resetAutoPlay();
        } else {
            stopAutoPlay();
        }
    }
}

onMounted(() => {
    startAutoPlay();
});

onUnmounted(() => {
    stopAutoPlay();
});
</script>

<template>
    <div
        v-if="hasBanners"
        class="banner-carousel"
        @mouseenter="pause"
        @mouseleave="resume"
    >
        <!-- Banner Display -->
        <div class="carousel-container">
            <SimpleBanner
                :banner="currentBanner"
                @click="handleBannerClick"
                @dismiss="handleBannerDismiss"
            />

            <!-- Navigation Arrows (only show for multiple banners) -->
            <template v-if="hasMultipleBanners">
                <!-- Previous Button -->
                <Button
                    icon="pi pi-chevron-left"
                    class="carousel-nav-btn prev-btn"
                    severity="secondary"
                    rounded
                    text
                    aria-label="Previous banner"
                    @click="prev"
                />

                <!-- Next Button -->
                <Button
                    icon="pi pi-chevron-right"
                    class="carousel-nav-btn next-btn"
                    severity="secondary"
                    rounded
                    text
                    aria-label="Next banner"
                    @click="next"
                />

                <!-- Indicators and Counter (inside banner, bottom center) -->
                <div class="carousel-bottom-controls">
                    <!-- Indicators -->
                    <div class="carousel-indicators">
                        <button
                            v-for="(banner, index) in visibleBanners"
                            :key="banner.id"
                            :class="['indicator', { active: index === currentIndex }]"
                            :aria-label="`Go to banner ${index + 1}`"
                            @click="goToSlide(index)"
                        />
                    </div>

                    <!-- Counter -->
                    <div class="carousel-counter">
                        {{ currentIndex + 1 }} / {{ visibleBanners.length }}
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped>
.banner-carousel {
    position: relative;
    margin-bottom: 1rem;
}

.carousel-container {
    position: relative;
    overflow: hidden;
}

/* Navigation Arrows - Left and Right edges, vertically centered */
.carousel-nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.5) !important;
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: all 0.2s ease;
    z-index: 10;
    opacity: 0.7;
}

.carousel-nav-btn:hover {
    background: rgba(255, 255, 255, 0.9) !important;
    transform: translateY(-50%) scale(1.1);
    opacity: 1;
}

.carousel-nav-btn.prev-btn {
    left: 1.5rem;
}

.carousel-nav-btn.next-btn {
    right: 1.5rem;
}

/* Bottom Controls Container - Horizontally centered at bottom */
.carousel-bottom-controls {
    position: absolute;
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 0.75rem;
    z-index: 10;
}

/* Indicators */
.carousel-indicators {
    display: flex;
    gap: 0.5rem;
    padding: 0.5rem;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 2rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.indicator {
    width: 2rem;
    height: 0.375rem;
    border: none;
    border-radius: 0.25rem;
    background: rgba(0, 0, 0, 0.2);
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 0;
}

.indicator:hover {
    background: rgba(0, 0, 0, 0.4);
}

.indicator.active {
    background: var(--primary-color);
    width: 2.5rem;
}

/* Counter */
.carousel-counter {
    padding: 0.5rem 1rem;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 2rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-color);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    white-space: nowrap;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .carousel-nav-btn.prev-btn {
        left: 0.5rem;
    }

    .carousel-nav-btn.next-btn {
        right: 0.5rem;
    }

    .carousel-bottom-controls {
        bottom: 1rem;
    }

    .carousel-counter {
        font-size: 0.75rem;
        padding: 0.375rem 0.75rem;
    }

    .indicator {
        width: 1.5rem;
        height: 0.3rem;
    }

    .indicator.active {
        width: 2rem;
    }
}

/* Animation for banner transitions */
.carousel-container :deep(.hero-banner-wrapper) {
    animation: fadeInSlide 0.5s ease-in-out;
}

@keyframes fadeInSlide {
    from {
        opacity: 0;
        transform: translateX(20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}
</style>
